import React, { useEffect, useRef, useState } from "react";

/**
 * Mui Components
 */
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";

/**
 * Utils
 */
import { isRoundNumber } from "utils/numberUtils";

/**
 * Third party libraries
 */
import { addDays } from "date-fns";
import { dateObjToDateStringDashedinverted } from "@thekilroygroup/shared-ui";
import { sxStyles } from "../sharedStyles";
import { IPriceItinerary } from "../interfaces.lowPrice";
import { DepartureNavigation } from "../Navigation/DepartureNavigation";
import { ReturnNavigation } from "../Navigation/ReturnNavigation";
import { DragContainer } from "../Navigation/DragContainer";
import { CalendarButton } from "../CalendarButton/CalendarButton";

interface IProps {
  cheapestCombinations: IPriceItinerary[];
  searchedDates: { departureDate: Date; returnDate: Date };
  selectedItineraryDates: { departureDate: string; returnDate: string } | null;
  setSelectedItinerary: (id: string) => void;
  selectedItineraryId: string | null;
  loadMoreData: (departureDate: Date, returnDate: Date) => void;
  showYouthStudentItineraries: boolean;
}

export const ReturnCalendar = (props: IProps) => {
  const {
    cheapestCombinations,
    searchedDates,
    selectedItineraryDates,
    showYouthStudentItineraries,
    loadMoreData = () => {},
  } = props;

  /**
   * Setup
   */
  const { breakpoints, spacing } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up("md"));
  const container = useRef<HTMLDivElement>(null);

  const size = {
    nav: isDesktop ? 5.25 : 3.75,
    date: isDesktop ? 5 : 3,
    returnDate: isDesktop ? 11 : 3,
  };

  const rows = 5;
  const columns = 5;
  const offset = 1;
  const width = 100 / columns;
  const height = 100 / rows;

  /**
   * Local state
   */
  const [minX, setMinX] = useState(-offset);
  const [maxX, setMaxX] = useState(columns + offset);
  const [minY, setMinY] = useState(-offset);
  const [maxY, setMaxY] = useState(rows + offset);

  const [transformX, setTransformX] = useState(0);
  const [transformY, setTransformY] = useState(0);

  const [xToLoad, setXtoLoad] = useState([0]);
  const [yToLoad, setYtoLoad] = useState([0]);

  const [maxMinPrice, setMaxMinPrice] = useState({ max: 0, min: 0 });
  const [fetchedCoordinates, setFetchedCoordinates] = useState<
    { x: number; y: number }[]
  >([{ x: 0, y: 0 }]);

  useEffect(() => {
    resetCalendar();
  }, [searchedDates]);

  /**
   * Functions
   */
  const resetCalendar = () => {
    setMinX(-offset);
    setMaxX(columns + offset);
    setMinY(-offset);
    setMaxY(rows + offset);
    setTransformX(0);
    setTransformY(0);
    setXtoLoad([0]);
    setYtoLoad([0]);
  };

  const handleLoad = (x: number, y: number) => {
    const isLoaded = fetchedCoordinates.some(
      (obj) => obj.x === x && obj.y === y,
    );

    if (!isLoaded) {
      loadMoreData(
        addDays(searchedDates.departureDate, x),
        addDays(searchedDates.returnDate, y),
      );

      setFetchedCoordinates((prevState) => [...prevState, { x: x, y: y }]);
    }
  };

  const handleTransformX = (number: number) => {
    const nextTransform = transformX + number;
    setTransformX(nextTransform);

    if (nextTransform > 0) {
      const xStateMax = (columns + 2 + nextTransform - offset) / (columns + 2);
      if (nextTransform + columns === maxX) {
        setMaxX(maxX + 1);
      }
      if (isRoundNumber(xStateMax)) {
        setXtoLoad((prevState) => [...prevState, nextTransform + 6]);

        yToLoad.forEach((yVal) => {
          handleLoad(nextTransform + 6, yVal);
        });
      }
    } else if (nextTransform < 0) {
      const xStateMin = (columns + 2 + nextTransform + offset) / (columns + 2);
      if (nextTransform === minX) {
        setMinX(minX - 1);
      }
      if (isRoundNumber(xStateMin)) {
        setXtoLoad((prevState) => [...prevState, nextTransform - 6]);
        yToLoad.forEach((yVal) => {
          handleLoad(nextTransform - 6, yVal);
        });
      }
    }
  };

  const handleTransformY = (number: number) => {
    const nextTransform = transformY + number;
    setTransformY(nextTransform);

    if (nextTransform > 0) {
      const yStateMax = (rows + 2 + nextTransform - offset) / (rows + 2);
      if (nextTransform + rows === maxY) {
        setMaxY(maxY + 1);
      }
      if (isRoundNumber(yStateMax)) {
        setYtoLoad((prevState) => [...prevState, nextTransform + 6]);
        xToLoad.forEach((xVal) => {
          handleLoad(xVal, nextTransform + 6);
        });
      }
    } else if (nextTransform < 0) {
      const yStateMin = (rows + 2 + nextTransform + offset) / (rows + 2);
      if (nextTransform === minY) {
        setMinY(minY - 1);
      }
      if (isRoundNumber(yStateMin)) {
        setYtoLoad((prevState) => [...prevState, nextTransform - 6]);
        xToLoad.forEach((xVal) => {
          handleLoad(xVal, nextTransform - 6);
        });
      }
    }
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (!cheapestCombinations.length) return;
    const nextCheapestCombinations = cheapestCombinations.filter(
      (obj: IPriceItinerary) => {
        return (
          obj.departureDateDifference < transformX + 3 &&
          obj.departureDateDifference > transformX - 3 &&
          obj.returnDateDifference < transformY + 3 &&
          obj.returnDateDifference > transformY - 3
        );
      },
    );

    const max = Math.max(
      ...nextCheapestCombinations.map((element: IPriceItinerary) => {
        if (element.price === -1) return 0;
        else if (!element.hasStudentOrYouth) return element.price;
        else if (showYouthStudentItineraries)
          return Math.max(element.price, element.studentOrYouthData.price);
        return element.price;
      }),
    );

    const min = Math.min(
      ...nextCheapestCombinations.map((element: IPriceItinerary) => {
        if (element.price === -1) return max;
        else if (!element.hasStudentOrYouth) return element.price;
        return Math.min(element.price, element.studentOrYouthData.price);
      }),
    );
    setMaxMinPrice({ max: max, min: min });
  }, [transformX, transformY, cheapestCombinations]);

  const sx = sxStyles;

  return (
    <Box sx={sx.calendar}>
      <Container ref={container} disableGutters sx={sx.container}>
        <DepartureNavigation
          size={size}
          cheapestCombinations={cheapestCombinations}
          selectedItineraryDates={selectedItineraryDates}
          width={width}
          handleTransformX={handleTransformX}
          transformX={transformX}
        />
        <Box
          className="grey-box"
          sx={{
            ...sx.greyBox,
            width: spacing(size.returnDate + size.nav),
            height: spacing(size.date + size.nav),
            bgcolor: "grey.100",
          }}
        >
          <Box
            sx={{
              ...sx.smallBox,
              height: spacing(size.date),
              width: spacing(size.returnDate),
            }}
          />
        </Box>
        <Box
          className="calendar-matrix"
          sx={{
            ...sx.matrix,
            height: `calc(100% - ${spacing(size.date + size.nav)}) `,
            width: `calc(100% - ${spacing(size.returnDate + size.nav)})`,
          }}
        >
          <DragContainer
            handleTransformX={handleTransformX}
            handleTransformY={handleTransformY}
          >
            <Box
              sx={{
                ...sx.wrapper,
                transform: `translate(${-transformX * width}%,${
                  -transformY * height
                }%)`,
              }}
            >
              {cheapestCombinations.map((obj: IPriceItinerary) => {
                if (
                  obj.departureDateDifference < transformX + 4 &&
                  obj.departureDateDifference > transformX - 4 &&
                  obj.returnDateDifference < transformY + 4 &&
                  obj.returnDateDifference > transformY - 4
                ) {
                  return (
                    <CalendarButton
                      key={
                        "loader" +
                        obj.departureDateDifference +
                        "-" +
                        obj.returnDateDifference +
                        "-" +
                        obj.id
                      }
                      priceItinerary={obj}
                      height={height}
                      width={width}
                      isSearchedDepartureDate={
                        obj.departureDate ===
                        dateObjToDateStringDashedinverted(
                          searchedDates.departureDate,
                        )
                      }
                      isSearchedReturnDate={
                        obj.returnDate ===
                        dateObjToDateStringDashedinverted(
                          searchedDates.returnDate,
                        )
                      }
                      maxMin={maxMinPrice}
                      {...props}
                    />
                  );
                }
              })}
            </Box>
          </DragContainer>
        </Box>
        <ReturnNavigation
          size={size}
          cheapestCombinations={cheapestCombinations}
          height={height}
          transformY={transformY}
          handleTransformY={handleTransformY}
          selectedItineraryDates={selectedItineraryDates}
        />
      </Container>
    </Box>
  );
};
